import { type FC, useState } from "react";
import { Form } from "react-bootstrap";
import type {
	MetaAdsCreateScheduleDto,
	MetaAdsReportSchedule,
	MetaAdsReportTemplate,
	MetaAdsScheduleFrequency,
} from "shared";
import { Modal, useModal } from "../../common/modal/Modal";
import { Select } from "../../components/Select";

const DAYS_OF_WEEK = [
	"Sunday",
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
];

interface ModalCreateScheduleProps {
	templates: MetaAdsReportTemplate[];
	onSave: (dto: MetaAdsCreateScheduleDto) => Promise<void>;
	initial?: MetaAdsReportSchedule;
}

export const ModalCreateSchedule: FC<ModalCreateScheduleProps> = ({
	templates,
	onSave,
	initial,
}) => {
	const { close } = useModal();
	const [templateId, setTemplateId] = useState(
		initial?.templateId ?? templates[0]?.id ?? 0,
	);
	const [frequency, setFrequency] = useState<MetaAdsScheduleFrequency>(
		initial?.frequency ?? "weekly",
	);
	const [dayOfWeek, setDayOfWeek] = useState(initial?.dayOfWeek ?? 1);
	const [dayOfMonth, setDayOfMonth] = useState(initial?.dayOfMonth ?? 1);
	const [recipientInput, setRecipientInput] = useState("");
	const [recipients, setRecipients] = useState<string[]>(
		initial?.recipients ?? [],
	);
	const [submitting, setSubmitting] = useState(false);

	const addRecipient = () => {
		const email = recipientInput.trim();
		if (email && !recipients.includes(email)) {
			setRecipients((prev) => [...prev, email]);
			setRecipientInput("");
		}
	};

	const removeRecipient = (email: string) => {
		setRecipients((prev) => prev.filter((r) => r !== email));
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			e.preventDefault();
			addRecipient();
		}
	};

	const handleSubmit = async () => {
		if (!templateId || recipients.length === 0) return;
		setSubmitting(true);
		try {
			await onSave({
				templateId,
				frequency,
				dayOfWeek: frequency === "weekly" ? dayOfWeek : undefined,
				dayOfMonth: frequency === "monthly" ? dayOfMonth : undefined,
				recipients,
			});
			close();
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal>
			<h5>{initial ? "Edit Schedule" : "Create Report Schedule"}</h5>

			<Form.Group className="mb-3">
				<Form.Label>Template</Form.Label>
				<Select
					value={templateId}
					onChange={(e) => setTemplateId(Number(e.target.value))}
				>
					{templates.map((t) => (
						<option key={t.id} value={t.id}>
							{t.name}
						</option>
					))}
				</Select>
			</Form.Group>

			<Form.Group className="mb-3">
				<Form.Label>Frequency</Form.Label>
				<Select
					value={frequency}
					onChange={(e) =>
						setFrequency(e.target.value as MetaAdsScheduleFrequency)
					}
				>
					<option value="daily">Daily</option>
					<option value="weekly">Weekly</option>
					<option value="monthly">Monthly</option>
				</Select>
			</Form.Group>

			{frequency === "weekly" && (
				<Form.Group className="mb-3">
					<Form.Label>Day of Week</Form.Label>
					<Select
						value={dayOfWeek}
						onChange={(e) => setDayOfWeek(Number(e.target.value))}
					>
						{DAYS_OF_WEEK.map((day, i) => (
							<option key={day} value={i}>
								{day}
							</option>
						))}
					</Select>
				</Form.Group>
			)}

			{frequency === "monthly" && (
				<Form.Group className="mb-3">
					<Form.Label>Day of Month</Form.Label>
					<Select
						value={dayOfMonth}
						onChange={(e) => setDayOfMonth(Number(e.target.value))}
					>
						{Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
							<option key={d} value={d}>
								{d}
							</option>
						))}
					</Select>
				</Form.Group>
			)}

			<Form.Group className="mb-3">
				<Form.Label>Recipients</Form.Label>
				<div className="d-flex gap-2 mb-2">
					<Form.Control
						type="email"
						value={recipientInput}
						onChange={(e) => setRecipientInput(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder="email@example.com"
					/>
					<button
						type="button"
						className="btn btn-outline-primary btn-sm"
						onClick={addRecipient}
					>
						Add
					</button>
				</div>
				<div className="d-flex flex-wrap gap-1">
					{recipients.map((email) => (
						<span
							key={email}
							className="badge bg-primary d-flex align-items-center gap-1"
						>
							{email}
							<button
								type="button"
								className="btn-close btn-close-white"
								style={{ fontSize: "0.5rem" }}
								onClick={() => removeRecipient(email)}
								aria-label={`Remove ${email}`}
							/>
						</span>
					))}
				</div>
				{recipients.length === 0 && (
					<Form.Text className="text-muted">
						Add at least one recipient email address.
					</Form.Text>
				)}
			</Form.Group>

			<div className="d-flex gap-2">
				<button
					type="button"
					className="btn btn-primary flex-grow-1"
					disabled={submitting || !templateId || recipients.length === 0}
					onClick={handleSubmit}
				>
					{submitting ? "Saving..." : "Save"}
				</button>
				<button
					type="button"
					className="btn btn-outline-secondary"
					onClick={close}
				>
					Cancel
				</button>
			</div>
		</Modal>
	);
};
